/**
 * Media URL Helper
 * Handles image URL normalization and fallbacks
 */

import { getApiBase } from '../api/httpClient';

const PREVIEW_HOST_RE =
  /\.(emergent\.host|emergentagent\.com|preview\.emergentagent\.com)$/i;

// iter426 — Google avatars (lh3.googleusercontent.com) load in Chrome but not
// inside the Android WebView on some devices/networks. Proxy them at DISPLAY
// TIME through the app's own Cloudinary CDN (fetch delivery) — no DB change,
// custom uploads keep precedence (they are Cloudinary URLs already). If fetch
// delivery is disabled on the Cloudinary account the <img> 401s and the
// initials fallback shows — never worse than before.
const CLOUDINARY_FETCH_BASE = 'https://res.cloudinary.com/dzgz0xb55/image/fetch/';
const GOOGLE_AVATAR_HOST_RE = /(^|\.)googleusercontent\.com$/i;

// iter421 — VERIFIED against the LIVE infrastructure (both preview and
// production wassla.tech, checked with curl on real files):
//
//     /api/uploads/<file>  → 200 image/*  (backend mount; the legacy upload
//                            files are committed to the repo and ship with
//                            every deploy, so this works everywhere)
//     /uploads/<file>      → 200 text/html (SPA index fallback — ALWAYS a
//                            broken image when used in <img src>)
//
// The old iter276 "strip /api on production custom domains" workaround
// targeted a long-gone ingress configuration and silently broke legacy
// avatars. The stable rule is simply: every uploads path must be served as
// {apiBase}/api/uploads/<file> — identical on web (same-origin base '') and
// inside the Android app (base = the baked production URL).

function normalizeUploadsPath(path) {
  if (path.startsWith('/uploads/')) return `/api${path}`;
  return path;
}

/**
 * Convert relative URL to absolute (or same-origin relative) URL.
 *
 * @param {string} url - Image URL (can be relative or absolute)
 * @returns {string} URL safe to use in <img src>
 */
export function toAbsoluteUrl(url) {
  if (!url) return '';

  // Same smart base as fetch(): '' (same-origin) on web domains,
  // the baked REACT_APP_BACKEND_URL inside the Android WebView.
  const base = getApiBase();

  // Already absolute (http(s)) → may still be a stale preview/emergent host
  // saved into the DB. Re-point those at the current API base; leave every
  // other absolute URL (Cloudinary, googleusercontent, …) untouched.
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const u = new URL(url);
      if (GOOGLE_AVATAR_HOST_RE.test(u.hostname)) {
        return `${CLOUDINARY_FETCH_BASE}${encodeURIComponent(url)}`;
      }
      if (typeof window !== 'undefined' && window.location) {
        if (u.origin !== window.location.origin && PREVIEW_HOST_RE.test(u.hostname)) {
          const pathOnly = u.pathname + (u.search || '') + (u.hash || '');
          return `${base}${normalizeUploadsPath(pathOnly)}`;
        }
      }
    } catch (_e) {
      // fall through and return URL untouched
    }
    return url;
  }
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  // Uploads paths — always the /api/uploads form (see note above).
  if (url.startsWith('/uploads/') || url.startsWith('/api/uploads/')) {
    return `${base}${normalizeUploadsPath(url)}`;
  }

  // Other relative paths
  if (url.startsWith('/')) {
    return `${base}${url}`;
  }

  // No leading slash
  return `${base}/${url}`;
}

/**
 * Get image URL with fallback
 * @param {string|string[]} imageSource - Single URL or array of URLs
 * @param {string} fallback - Fallback image URL
 * @returns {string} Image URL or fallback
 */
export function getImageUrl(imageSource, fallback = '/placeholder.svg') {
  if (!imageSource) return fallback;

  // If array, get first item
  const url = Array.isArray(imageSource) ? imageSource[0] : imageSource;

  if (!url) return fallback;

  return toAbsoluteUrl(url);
}

/**
 * Handle image load error - sets fallback
 * @param {Event} e - Error event
 * @param {string} fallback - Fallback image URL
 */
export function handleImageError(e, fallback = '/placeholder.svg') {
  e.currentTarget.onerror = null; // Prevent infinite loop
  e.currentTarget.src = fallback;
}

export default {
  toAbsoluteUrl,
  getImageUrl,
  handleImageError
};
